import React from 'react';
import { MessageSquare, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { QUALITY_LABELS } from '../../utils/quality-labels';
import type { SessionSummary } from '../../services/api/sessions';
import { resolveSessionDomainLabel } from './session-domain';

interface SessionCardProps {
  session: SessionSummary;
  onSelect: (session: SessionSummary) => void;
  isSelected: boolean;
}

const analyzedAtFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm',
  hour: '2-digit',
  minute: '2-digit',
});

const stockholmDateTimeFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm',
  dateStyle: 'short',
  timeStyle: 'short',
});

export const SessionCard: React.FC<SessionCardProps> = ({ session, onSelect, isSelected }) => {
  const now = new Date();

  const relativeLabel = (iso: string): string => {
    const diffMinutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60000);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const hours = Math.floor(diffMinutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const analyzedAtDate = session.analyzedAt ? new Date(session.analyzedAt) : null;
  const hasValidAnalyzedAt = Boolean(analyzedAtDate && !Number.isNaN(analyzedAtDate.getTime()));

  const getChannelIcon = () => <MessageSquare className="w-4 h-4 text-slate-600" />;

  const domainLabel = resolveSessionDomainLabel(session);

  return (
    <button
      onClick={() => onSelect(session)}
      className={cn(
        'w-full text-left rounded-lg border transition-all duration-200 p-4',
        'hover:shadow-md active:scale-98',
        isSelected
          ? 'bg-blue-50 border-blue-300 shadow-md'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {getChannelIcon()}
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {session.channel}
            </span>
            {domainLabel && (
              <span
                title={domainLabel.title}
                className={cn(
                  'inline-block px-1.5 py-0.5 text-xs rounded font-mono',
                  domainLabel.isProxy
                    ? 'bg-slate-100 text-slate-500'
                    : 'bg-indigo-100 text-indigo-700 font-medium',
                )}
              >
                {domainLabel.label}
              </span>
            )}
            <span className="text-xs text-slate-400">•</span>
            <span
              className="text-xs text-slate-400"
              title={`Samtalet startade ${stockholmDateTimeFormatter.format(new Date(session.firstSeen))}`}
            >
              {relativeLabel(session.firstSeen)}
            </span>
            {hasValidAnalyzedAt && (
              <>
                <span className="text-xs text-slate-400">•</span>
                <span
                  className="text-xs text-slate-400"
                  title={`Sessionen analyserades ${stockholmDateTimeFormatter.format(analyzedAtDate!)}`}
                >
                  analyserad {analyzedAtFormatter.format(analyzedAtDate!)}
                </span>
              </>
            )}
          </div>

          <div className="mb-2">
            <code
              className="inline-block px-2 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded cursor-pointer hover:bg-slate-200 transition-colors"
              title="Click to copy"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(session.sessionId);
              }}
            >
              {session.sessionId}
            </code>
          </div>

          <p className="text-sm font-medium text-slate-900 line-clamp-2 mb-2">
            {session.firstMessage || '(no message)'}
          </p>

          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span>{session.requestCount} reqs</span>
            {session.totalMs && <span>{session.totalMs}ms</span>}
            {session.intents.length > 0 && (
              <span className="text-blue-600 font-medium">{session.intents.length} intents</span>
            )}
          </div>

          {session.intents.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {session.intents.slice(0, 3).map((intent) => (
                <span
                  key={intent}
                  className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                >
                  {intent}
                </span>
              ))}
              {session.intents.length > 3 && (
                <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                  +{session.intents.length - 3}
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-2">
            {session.hasOutfit && (
              <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded font-medium">
                Outfit
              </span>
            )}
            {session.isVerified && (
              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">
                Verified
              </span>
            )}
            {session.analysisQuality === 'poor' && (
              <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-medium">
                {QUALITY_LABELS.poor}
              </span>
            )}
            {session.analysisQuality === 'degraded' && (
              <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded font-medium">
                {QUALITY_LABELS.degraded}
              </span>
            )}
            {session.analysisQuality === 'good' && (
              <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-medium">
                {QUALITY_LABELS.good}
              </span>
            )}
            {session.analysisQuality === 'insufficient' && (
              <span
                className="inline-block px-2 py-1 bg-sky-100 text-sky-800 text-xs rounded font-medium"
                title="Sessionen har högst en kundtur — för kort att bedöma, inget kvalitetsomdöme"
              >
                {QUALITY_LABELS.insufficient}
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0">
          <ChevronRight
            className={cn(
              'w-5 h-5 transition-transform duration-200',
              isSelected ? 'text-blue-600 transform rotate-90' : 'text-slate-400',
            )}
          />
        </div>
      </div>
    </button>
  );
};
